'use client';

import { useCallback, useEffect, useState } from 'react';

const WEBSITE_ID = '87a5f33f-bf77-d39d-7056-6afb8f8bab50';
const BOT_HEIGHT = 'large';
const CHAT_BASE = 'https://aianalytics.locobuzz.com/ChatBotWebPlugin/DynamicIndex';
const DEPLOYED_SITE = 'https://locobuzz.com/';

/** Hostnames where the parent page is the live site (pass real parenturl). */
const PRODUCTION_HOSTS = new Set(['locobuzz.com', 'www.locobuzz.com']);

/**
 * The Locobuzz bot is registered for WebsiteUrl=locobuzz.com. Any other host
 * (localhost, preview URLs, staging) must present hostname=locobuzz.com and a
 * matching parenturl — otherwise the server returns RedirectHost, which omits
 * LocoGlobalFirstName / firebase and throws on click.
 */
function resolveChatParams() {
  const onProduction = PRODUCTION_HOSTS.has(window.location.hostname);
  return {
    hostname: 'locobuzz.com',
    parenturl: onProduction ? window.location.href : DEPLOYED_SITE,
  };
}

function seedChatStorage() {
  try {
    if (!localStorage.getItem('LocobuzzuserHeight')) {
      localStorage.setItem('LocobuzzuserHeight', BOT_HEIGHT);
    }
  } catch {
    /* private browsing */
  }
}

function applyCollapsedIframeStyles() {
  const iframe = document.getElementById('Locobuzz_chat_windowIframe');
  const host = document.getElementById('Locobuzz_Chat');
  if (!iframe || !host) return;

  const mobile = window.matchMedia('(max-width: 700px)').matches;
  const chatposition = mobile ? 'bottom: 0; right: 0;' : 'bottom: 70px; right: 0px;';
  const cssString =
    'border: none;width:300px; display: block; height: 120px !important;110px !important; '
    + 'position: fixed; top: auto; left: auto; visibility: visible; z-index: 2147483646; '
    + 'max-height: 100vh; max-width: 100vw; background: none transparent; opacity: 1; '
    + 'pointer-events: auto; touch-action: auto;transform:translateX(100%);'
    + chatposition;

  host.style.position = 'absolute';
  iframe.style.cssText = cssString;
}

function buildChatIframeSrc() {
  seedChatStorage();

  const { hostname, parenturl } = resolveChatParams();
  const firstname = localStorage.getItem('Locobuzzfirstname') || '';
  const lastname = localStorage.getItem('Locobuzzlastname') || '';
  const senderId = localStorage.getItem('LocobuzzsenderId') || '';
  const referenceName = localStorage.getItem('LocobuzzreferenceName') || '';
  const userHeight = localStorage.getItem('LocobuzzuserHeight') || BOT_HEIGHT;

  const params = new URLSearchParams({
    websiteID: WEBSITE_ID,
    reference: WEBSITE_ID,
    hostname,
    senderid: senderId,
    firstname,
    lastname,
    refname: referenceName,
    userheight: userHeight,
    parenturl,
  });

  return `${CHAT_BASE}?${params.toString()}`;
}

function mountChatIframe() {
  const mount = document.getElementById('Locobuzz_Chat');
  if (!mount) return;

  const src = buildChatIframeSrc();
  const existing = mount.querySelector('#Locobuzz_chat_windowIframe');

  // Already mounted — do not re-apply collapsed styles (translateX(100%)) or the
  // bubble will disappear again after the iframe's RemoveTransition slide-in.
  if (existing?.getAttribute('src') === src) return;

  mount.innerHTML =
    `<iframe src="${src}" id="Locobuzz_chat_windowIframe" `
    + 'title="Locobuzz chat" '
    + 'style="height: 100%; width: 100%; border: 0px; margin-bottom: -10px;" '
    + 'allow="geolocation; microphone; camera" '
    + 'referrerpolicy="no-referrer-when-downgrade"></iframe>';

  applyCollapsedIframeStyles();
}

function handleChatMessage(evt) {
  if (!evt?.data || typeof evt.data !== 'object') return;

  const agentdata = evt.data;
  const iframe = document.getElementById('Locobuzz_chat_windowIframe');
  if (!iframe) return;

  if (agentdata.eventname === 'increaseheight') {
    const mobile = window.matchMedia('(max-width: 700px)').matches;
    let chatposition = '';

    if (mobile) {
      chatposition = 'bottom: 0; right: 0;height: 100% !important;width: 100% !important;';
    } else if (agentdata.userheight === 'large') {
      localStorage.setItem('LocobuzzuserHeight', 'large');
      chatposition =
        'bottom: 70px; right: 0px;height: calc(100% - 70px) !important;width: 400px !important;';
    } else if (agentdata.userheight === 'small') {
      localStorage.setItem('LocobuzzuserHeight', 'small');
      chatposition =
        'bottom: 70px; right: 0px;height: calc(80% - 70px) !important;width: 400px !important;';
    } else {
      chatposition =
        'bottom: 70px; right: 0px;height: calc(100% - 70px) !important;width: 400px !important;';
    }

    iframe.style.cssText =
      'border: none;display: block;position: fixed;top: auto;left: auto;visibility: visible;'
      + 'z-index: 2147483646;max-height: 100vh;max-width: 100vw;background: none transparent;'
      + 'opacity: 1;pointer-events: auto;touch-action: auto;'
      + chatposition;
    return;
  }

  if (agentdata.eventname === 'decreaseheight') {
    applyCollapsedIframeStyles();
    return;
  }

  if (agentdata.eventname === 'RemoveTransition') {
    iframe.style.transform = 'translateX(0)';
    setTimeout(() => {
      iframe.style.transition = 'none';
    }, 400);

    iframe.contentWindow?.postMessage(
      {
        eventname: 'GetParentHeight',
        ParentHeight: window.matchMedia('(max-width: 700px)').matches,
      },
      '*',
    );
    return;
  }

  if (agentdata.eventname === 'locouserinfo') {
    try {
      localStorage.setItem('Locobuzzfirstname', agentdata.firstname);
      localStorage.setItem('Locobuzzlastname', agentdata.lastname);
      localStorage.setItem('LocobuzzsenderId', agentdata.SenderId);
      localStorage.setItem('LocobuzzreferenceName', agentdata.refName);
    } catch {
      /* ignore */
    }
  }
}

/** Landing pages where chat (and its ~3MB emoji CSS) must not load at all. */
const SKIP_CHAT_PREFIXES = [
  '/lp/en/customer-experience-platform-middle-east',
  '/lp/ar/customer-experience-platform-middle-east',
];

function shouldSkipChat() {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  return SKIP_CHAT_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

/**
 * Chat loads only on click of the stub bubble — never on idle timers.
 * The aianalytics iframe pulls jquery-emoji-picker CSS (~3MB); keeping it
 * click-gated keeps that out of PageSpeed / Lighthouse lab windows.
 * ME CX landing pages skip chat entirely (see SKIP_CHAT_PREFIXES).
 */
export default function LocobuzzChat() {
  const [armed, setArmed] = useState(false);
  const [gate, setGate] = useState('pending'); // pending | skip | show

  const arm = useCallback(() => {
    setArmed(true);
  }, []);

  useEffect(() => {
    setGate(shouldSkipChat() ? 'skip' : 'show');
  }, []);

  useEffect(() => {
    if (gate !== 'show' || !armed) return undefined;

    mountChatIframe();
    window.addEventListener('message', handleChatMessage, false);
    return () => {
      window.removeEventListener('message', handleChatMessage, false);
    };
  }, [armed, gate]);

  if (gate === 'pending' || gate === 'skip') return null;

  return (
    <>
      <div id="Locobuzz_Chat" />
      {!armed && (
        <button
          type="button"
          aria-label="Open chat"
          onClick={arm}
          style={{
            position: 'fixed',
            right: 16,
            bottom: 24,
            zIndex: 2147483645,
            width: 56,
            height: 56,
            borderRadius: '50%',
            border: 'none',
            cursor: 'pointer',
            background: 'linear-gradient(135deg, #0098FF 0%, #0057D4 100%)',
            boxShadow: '0 8px 24px rgba(0, 87, 212, 0.35)',
            color: '#fff',
            fontSize: 22,
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7a2.5 2.5 0 0 1-2.5 2.5H9l-3.8 3.2c-.7.6-1.7.1-1.7-.8V6.5Z"
              fill="currentColor"
            />
          </svg>
        </button>
      )}
    </>
  );
}
